import React, { useCallback } from 'react';
import { Control, FieldPathByValue, useFieldArray } from 'react-hook-form';
import { PlusIcon, Trash2 } from 'lucide-react';

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { buttonVariants } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';

import { ICheckList } from '../schema';
import FormEntry from './FormEntry';

interface IProps {
  control: Control<ICheckList>;
  name: FieldPathByValue<ICheckList, ICheckList['steps'][0]>;
  onRemove: React.MouseEventHandler<HTMLButtonElement>;
}

const StepForm: React.FC<IProps> = ({ control, name, onRemove }) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: `${name}.form`,
  });

  const handleAppend = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      append({ name: '', type: 'text', required: false });
    },
    [append],
  );

  const handleRemove = useCallback(
    (index: number) => (e: React.MouseEvent) => {
      e.stopPropagation();
      remove(index);
    },
    [remove],
  );

  return (
    <div>
      <div className="flex w-full gap-4 justify-between">
        <FormField
          control={control}
          name={`${name}.name`}
          render={({ field, fieldState: { invalid } }) => (
            <Input
              placeholder="Nome da etapa"
              className="max-w-xs w-full"
              onClick={(e) => e.stopPropagation()}
              error={invalid}
              {...field}
            />
          )}
        />
        <div className="flex gap-4">
          <span
            className={buttonVariants({ variant: 'outline', size: 'icon' })}
            onClick={handleAppend}
          >
            <PlusIcon className="h-4 w-4" />
          </span>

          <span
            className={buttonVariants({
              variant: 'destructive',
              size: 'icon',
            })}
            onClick={onRemove}
          >
            <Trash2 className="h-4 w-4" />
          </span>
        </div>
      </div>
      <div className="flex flex-col gap-4 pl-8">
        <FormField
          control={control}
          name={`${name}.description`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Descrição</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Descrição da etapa"
                  className="resize-y"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name={`${name}.showPassengerCounter`}
          render={({ field }) => (
            <div className="flex items-center space-x-2">
              <Checkbox
                id={`${name}.showPassengerCounter`}
                checked={field.value}
                onCheckedChange={field.onChange}
              />
              <label
                htmlFor={`${name}.showPassengerCounter`}
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Exibir contador de passageiros
              </label>
            </div>
          )}
        />

        {fields.length > 0 && (
          <h4 className="font-medium">Campos do formulário</h4>
        )}
        {fields.map(({ id }, index) => (
          <FormEntry
            key={id}
            control={control}
            name={`${name}.form.${index}`}
            onRemove={handleRemove(index)}
            withSeparetor={index > 0}
          />
        ))}
      </div>
    </div>
  );
};

export default StepForm;
